import { useCallback, useMemo, useState } from "react";
import {
  Anchor,
  Box,
  Button,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../../../utils/notify";

import type { FormImagePointer } from "../../../types/formTemplates";
import {
  isFormImagePointer,
  isTombstonedPointer,
  readPointerDocumentUuid,
  readPointerFilename,
  readPointerS3Key,
} from "../../../utils/formImagePointer";
import {
  formatDocumentByteSize,
  documentListDateShort,
} from "../../../utils/clientDocumentUploadKey";
import { useApp } from "../../../context/AppContext";
import type { ImageInfoModel } from "../../clients/charts/ImageInfoPanel";
import type { ChartDocument } from "../../clients/charts/types/chart";
import ChartEditor from "../../clients/charts/ChartEditor";
import { exportChartAsBlob } from "../../clients/charts/utils/chartExport";
import type { DocumentVersion } from "../../../types/salonDocument";
import Konva from "konva";
import { FormImageViewer } from "./FormImageViewer";

/** Anything that may sit inside an image_upload field's array. */
export type ImageUploadEntry = string | FormImagePointer;

export interface ImageUploadFieldProps {
  label: string;
  required: boolean;
  isReadOnly: boolean;
  maxFiles: number | null | undefined;
  /**
   * The raw image_upload value normalised to a list. Either bare-string
   * S3 keys (legacy data) or pointer dicts (post-dual-write). Mixed lists
   * are tolerated during the migration window.
   */
  existing: ImageUploadEntry[];
  uploading: boolean;
  uploadReadLoading: boolean;
  urlByS3Key: Record<string, string>;
  errorText?: string;
  actorBadge: React.ReactNode;
  onUpload: (file: File, append: boolean) => void;
  /**
   * Called with the entry the user wants to remove. Parent decides
   * whether this is a server-side soft-delete (pointer with backend
   * row) or a local-only array splice (legacy bare string).
   */
  onRemove: (entry: ImageUploadEntry) => void | Promise<void>;
  /**
   * Patch the pointer's fields in place. Used after markup save to
   * swap the pointer's ``s3_key`` to the new salon_documents version
   * key — that's what flips the form's inline preview to the
   * marked-up image on the next render.
   *
   * The parent (FormViewer) wires this to its setData so the change
   * lands in client_forms.data_json on the next autosave.
   */
  onPointerUpdate?: (
    entry: ImageUploadEntry,
    updates: Partial<FormImagePointer>,
  ) => void;
  /**
   * When provided AND the active entry is a pointer with
   * salon_document_uuid, clicking the inline image opens the markup
   * ChartEditor (full-screen Modal). Without this context, the lightbox
   * falls back to a plain Mantine Modal preview (PR #295 behaviour).
   */
  markupContext?: {
    salonId: string;
    clientId: string;
    clientDisplayName?: string;
  };
}

const THUMB_SIZE = 64;
const THUMB_VISIBLE_LIMIT = 5;

export function ImageUploadField({
  label,
  required,
  isReadOnly,
  maxFiles,
  existing,
  uploading,
  uploadReadLoading,
  urlByS3Key,
  errorText,
  actorBadge,
  onUpload,
  onRemove,
  onPointerUpdate,
  markupContext,
}: ImageUploadFieldProps) {
  // Only the two methods the markup flow actually calls; the form's (X)
  // Remove is wired in FormViewer and doesn't need anything from here.
  const { uploadSalonDocument, getSalonDocumentDetail } = useApp();

  // Render only the non-tombstoned entries; backend keeps deleted ones
  // in the array for audit, but the carousel treats them as gone.
  const visible = useMemo(
    () => existing.filter((e) => !isTombstonedPointer(e)),
    [existing],
  );

  const [activeIdx, setActiveIdx] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerBlobUrl, setViewerBlobUrl] = useState<string | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [imageDims, setImageDims] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // Canonical title for the salon_documents row backing the active
  // pointer. Form data_json's pointer.filename can drift from the
  // salon_documents.displayName once the doc is renamed from the
  // Documents tab. We prefer the salon_documents value so the title
  // shown in the form viewer matches what staff see in Documents — and
  // round-trip through markup save updates BOTH (`uploadSalonDocument`
  // sets displayName; `onPointerUpdate` patches filename).
  const [markupDisplayName, setMarkupDisplayName] = useState<string | null>(
    null,
  );
  // Markup editor lives in a SEPARATE fullscreen modal that mounts on
  // top of the carousel viewer, matching the Documents tab + Medical
  // Charts pattern: bare ChartEditor, auto-close on save.
  const [markupOpen, setMarkupOpen] = useState(false);

  const count = visible.length;
  const safeIdx = count === 0 ? 0 : Math.min(activeIdx, count - 1);
  const activeEntry = count > 0 ? visible[safeIdx] : undefined;
  const activeS3Key = readPointerS3Key(activeEntry);
  const isPhi =
    typeof activeS3Key === "string" && activeS3Key.startsWith("phi/");
  const activePreviewUrl =
    isPhi && activeS3Key ? urlByS3Key[activeS3Key] : undefined;
  const activeFilename = readPointerFilename(activeEntry);
  const allowMore = !maxFiles || count < maxFiles;
  const isMulti = maxFiles !== 1;

  // Pointer with materialized salon_documents row → markup-eligible.
  const activeDocUuid = readPointerDocumentUuid(activeEntry);
  const activeDocKey =
    isFormImagePointer(activeEntry) && activeEntry.document_key
      ? activeEntry.document_key
      : null;
  // Eligibility is conceptual ("can this entry be marked up?") — it
  // must NOT depend on whether the presigned URL has been resolved yet.
  // After a markup save the pointer's s3_key changes; urlByS3Key is
  // briefly missing the new key while /uploads/read refetches, which
  // used to flip markupEligible false→true and tear down ChartEditor's
  // version sidebar mid-save (the "disappear + reappear" Joseph
  // diagnosed). The openViewer flow handles a missing URL separately.
  const markupEligible = Boolean(
    markupContext && activeDocUuid && activeDocKey,
  );

  const openViewer = useCallback(
    async (idx: number) => {
      if (viewerLoading) return;
      setActiveIdx(idx);
      const entry = visible[idx];
      const key = readPointerS3Key(entry);
      const url = key ? urlByS3Key[key] : undefined;
      // Kick off the salon_documents detail fetch in parallel with the
      // image blob fetch so the canonical title is ready before
      // ChartEditor mounts. Without this the form would show
      // pointer.filename while the Documents tab shows displayName,
      // which can diverge if anyone has renamed the doc.
      const docUuid = readPointerDocumentUuid(entry);
      const docKey =
        isFormImagePointer(entry) && entry.document_key
          ? entry.document_key
          : null;
      if (markupContext && docUuid && docKey) {
        void getSalonDocumentDetail(
          markupContext.salonId,
          docKey,
          markupContext.clientId,
        )
          .then((detail) => {
            const next = detail.displayName?.trim();
            if (next) setMarkupDisplayName(next);
          })
          .catch(() => {
            /* non-fatal — fall back to pointer.filename */
          });
      }
      if (!url) {
        setViewerOpen(true);
        return;
      }
      setViewerLoading(true);
      setImageDims(null);
      try {
        const res = await fetch(url, { credentials: "omit" });
        if (!res.ok) throw new Error(`Failed to load image (${res.status})`);
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        setViewerBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objUrl;
        });
        const img = new globalThis.Image();
        img.onload = () => {
          setImageDims({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          img.onload = null;
        };
        img.src = objUrl;
        setViewerOpen(true);
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Unknown error.", {
          title: "Could not open image",
        });
      } finally {
        setViewerLoading(false);
      }
    },
    [visible, urlByS3Key, viewerLoading, markupContext, getSalonDocumentDetail],
  );

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    if (viewerBlobUrl) {
      URL.revokeObjectURL(viewerBlobUrl);
      setViewerBlobUrl(null);
    }
    setImageDims(null);
    setMarkupDisplayName(null);
  }, [viewerBlobUrl]);

  const handleRemove = useCallback(
    async (entry: ImageUploadEntry) => {
      await onRemove(entry);
      // Keep activeIdx valid after the parent mutates `existing`.
      setActiveIdx((i) => Math.max(0, Math.min(i, count - 2)));
      if (count - 1 <= 0) closeViewer();
    },
    [onRemove, count, closeViewer],
  );

  const handleMarkupSave = useCallback(
    async (_doc: ChartDocument, stage: Konva.Stage) => {
      if (!markupContext || !activeDocKey || !activeEntry) return;
      const blob = await exportChartAsBlob(stage);
      // Use the user's edited title from ChartEditor (if any) as the
      // new salon_documents displayName so Documents tab and the form
      // viewer stay in sync. Fall back to the canonical fetched name,
      // then the pointer's filename.
      const editedTitle = _doc.metadata?.title?.trim();
      const newDisplayName =
        editedTitle || markupDisplayName || activeFilename || undefined;
      const response = await uploadSalonDocument(
        markupContext.salonId,
        activeDocKey,
        blob,
        "image/png",
        { source: "form_image_markup" },
        markupContext.clientId,
        newDisplayName,
      );
      // Backend may return s3Key (camelCase) or s3_key (snake_case)
      // depending on the serializer; tolerate both.
      const newS3Key =
        (response as { s3Key?: string; s3_key?: string } | undefined)?.s3Key ??
        (response as { s3Key?: string; s3_key?: string } | undefined)?.s3_key;

      // Swap the carousel's blob to the just-marked-up image and
      // remeasure dimensions so when the markup modal closes and we're
      // back in the viewer, the new image is on screen immediately
      // (the form's /uploads/read refetch needs a moment to populate
      // urlByS3Key with the new salon_documents version path).
      try {
        const objUrl = URL.createObjectURL(blob);
        const img = new globalThis.Image();
        img.onload = () => {
          setImageDims({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          img.onload = null;
        };
        img.src = objUrl;
        setViewerBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objUrl;
        });
      } catch {
        /* non-fatal — backend already has the version */
      }

      // Flip the form's data_json pointer to the new salon_documents
      // version's s3_key and patch the pointer's filename to match
      // whatever the user typed as the title.
      if (newS3Key) {
        const updates: Partial<FormImagePointer> = {
          s3_key: newS3Key,
          content_type: "image/png",
        };
        if (editedTitle) updates.filename = editedTitle;
        onPointerUpdate?.(activeEntry, updates);
      }
      if (editedTitle) setMarkupDisplayName(editedTitle);
      notifySuccess("A new version was added to the Documents tab.", {
        title: "Markup saved",
      });

      // Auto-close the markup modal — matches Documents tab + Medical
      // Charts UX. ChartEditor's subsequent markClean + refreshVersions
      // run on the closing component; React 18 silently discards state
      // updates on the unmounting tree.
      setMarkupOpen(false);
    },
    [
      markupContext,
      activeDocKey,
      activeEntry,
      uploadSalonDocument,
      markupDisplayName,
      activeFilename,
      onPointerUpdate,
    ],
  );

  const handleLoadMarkupVersions = useCallback(async () => {
    if (!markupContext || !activeDocKey) {
      return { versions: [] as DocumentVersion[] };
    }
    const detail = await getSalonDocumentDetail(
      markupContext.salonId,
      activeDocKey,
      markupContext.clientId,
    );
    return {
      versions: detail.versions ?? [],
      currentVersionId: detail.currentVersionId,
    };
  }, [markupContext, activeDocKey, getSalonDocumentDetail]);

  const handleLoadMarkupVersion = useCallback(
    async (version: DocumentVersion): Promise<ChartDocument | null> => {
      if (!markupContext || !activeDocKey) return null;
      const url = version.url;
      if (!url) return null;
      try {
        const res = await fetch(url, {
          mode: "cors",
          credentials: "omit",
          cache: "no-store",
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        // Sync the carousel preview blob too so when the user closes
        // the markup modal they see the restored version, not the
        // pre-restore image.
        const objUrl = URL.createObjectURL(blob);
        setViewerBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objUrl;
        });
        // Hand ChartEditor a real ChartDocument keyed off this blob so
        // loadDocument actually wipes the session strokes and swaps the
        // baseline — previously this returned null, which made Restore
        // a no-op for the canvas and left current strokes drawn over
        // the restored image.
        return {
          schemaVersion: 1,
          baseImageRef: objUrl,
          baseImageSource: "url",
          strokes: [],
          metadata: {
            title: (version.displayName?.trim() || activeFilename) ?? undefined,
          },
        };
      } catch {
        return null;
      }
    },
    [markupContext, activeDocKey, activeFilename],
  );

  // Info drawer model — built from whatever pointer fields exist.
  // Shown for every entry (bare-string legacy too); fields the pointer
  // doesn't carry render as "—" in the panel.
  const infoModel: ImageInfoModel | undefined = useMemo(() => {
    if (!activeEntry) return undefined;
    const ptr = isFormImagePointer(activeEntry) ? activeEntry : null;
    return {
      fileName:
        markupDisplayName ??
        activeFilename ??
        (ptr ? ptr.s3_key : (activeEntry as string)),
      description: null,
      uploadedBy: null,
      uploadDate:
        ptr && ptr.uploaded_at ? documentListDateShort(ptr.uploaded_at) : null,
      fileType: ptr?.content_type ?? null,
      dimensions: imageDims,
      formattedSize:
        ptr && typeof ptr.file_size === "number"
          ? formatDocumentByteSize(ptr.file_size)
          : null,
    };
  }, [activeEntry, activeFilename, imageDims, markupDisplayName]);

  const handleDownloadActive = useCallback(() => {
    if (!viewerBlobUrl) return;
    const a = document.createElement("a");
    a.href = viewerBlobUrl;
    a.download = activeFilename ?? "image";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [viewerBlobUrl, activeFilename]);

  const handleDownloadAll = useCallback(() => {
    // Use the resolved presigned URLs; one click per image. Browsers
    // generally coalesce identical-filename downloads — different
    // filenames are fine.
    for (const e of visible) {
      const key = readPointerS3Key(e);
      const url = key ? urlByS3Key[key] : undefined;
      if (!url) continue;
      const a = document.createElement("a");
      a.href = url;
      a.download = readPointerFilename(e) ?? "image";
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }, [visible, urlByS3Key]);

  // ------- Thumbnail strip render helpers ----------------------------
  const renderThumb = (entry: ImageUploadEntry, idx: number) => {
    const key = readPointerS3Key(entry);
    const url = key ? urlByS3Key[key] : undefined;
    const fname = readPointerFilename(entry) ?? "image";
    const isActive = idx === safeIdx && viewerOpen;
    return (
      <Box
        key={key ?? idx}
        component="button"
        type="button"
        onClick={() => {
          void openViewer(idx);
        }}
        aria-label={`Preview ${fname}`}
        style={{
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          borderRadius: 8,
          padding: 0,
          border: isActive
            ? "2px solid var(--mantine-color-blue-5)"
            : "1px solid var(--mantine-color-gray-3)",
          background: "var(--mantine-color-gray-0)",
          overflow: "hidden",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        {url ? (
          <Image
            src={url}
            alt={fname}
            fit="cover"
            w={THUMB_SIZE}
            h={THUMB_SIZE}
            style={{ display: "block" }}
          />
        ) : uploadReadLoading ? (
          <Group justify="center" align="center" h={THUMB_SIZE}>
            <Loader size="xs" />
          </Group>
        ) : (
          <Group justify="center" align="center" h={THUMB_SIZE}>
            <Text size="xs" c="dimmed">
              ?
            </Text>
          </Group>
        )}
      </Box>
    );
  };

  const thumbStrip =
    count > THUMB_VISIBLE_LIMIT ? (
      // Horizontal scroll for >5 images — keeps the row visually compact.
      <ScrollArea type="auto" offsetScrollbars>
        <Group gap={6} wrap="nowrap">
          {visible.map(renderThumb)}
        </Group>
      </ScrollArea>
    ) : (
      <Group gap={6} wrap="wrap">
        {visible.map(renderThumb)}
      </Group>
    );
  // -------------------------------------------------------------------

  return (
    <Stack gap={6}>
      <Group gap="xs">
        <Text size="sm" fw={500}>
          {label}
          {required && (
            <Text span c="red" ml={4}>
              *
            </Text>
          )}
        </Text>
        {actorBadge}
      </Group>

      {count > 0 && (
        <Group gap="md" align="center" wrap="nowrap">
          <Box style={{ flex: 1, minWidth: 0 }}>{thumbStrip}</Box>
          {count > 1 && (
            <Anchor
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                handleDownloadAll();
              }}
              href="#"
            >
              Download All
            </Anchor>
          )}
        </Group>
      )}

      {!isReadOnly && allowMore && (
        <FileButton
          accept="image/*"
          multiple={isMulti}
          onChange={(files) => {
            if (!files) return;
            const list = Array.isArray(files) ? files : [files];
            if (list.length === 0) return;
            const remaining = maxFiles
              ? Math.max(0, maxFiles - count)
              : list.length;
            const append = isMulti;
            list.slice(0, remaining).forEach((f, i) => {
              onUpload(f, append || i > 0);
            });
            setActiveIdx(count);
          }}
        >
          {(props) => (
            <Button
              {...props}
              size="xs"
              variant="light"
              loading={uploading}
              w="fit-content"
            >
              {count === 0
                ? "Upload image"
                : maxFiles === 1
                  ? "Replace image"
                  : "Add another image"}
            </Button>
          )}
        </FileButton>
      )}

      {errorText && (
        <Text size="xs" c="red">
          {errorText}
        </Text>
      )}

      {viewerLoading && (
        <Text size="xs" c="dimmed">
          Opening image…
        </Text>
      )}

      {/* Full-screen carousel viewer. */}
      {viewerOpen && activeEntry !== undefined && (
        <FormImageViewer
          opened={viewerOpen}
          onClose={closeViewer}
          baseImageUrl={viewerBlobUrl ?? activePreviewUrl ?? ""}
          // Prefer the salon_documents canonical displayName when we
          // have it — same source the Documents tab + Medical Charts
          // use.
          title={markupDisplayName ?? activeFilename ?? "Uploaded image"}
          infoModel={infoModel}
          hasPrev={count > 1}
          hasNext={count > 1}
          onPrev={() => {
            const next = (safeIdx - 1 + count) % count;
            void openViewer(next);
          }}
          onNext={() => {
            const next = (safeIdx + 1) % count;
            void openViewer(next);
          }}
          onDownload={handleDownloadActive}
          onDelete={isReadOnly ? undefined : () => handleRemove(activeEntry)}
          onTitleChange={(nextTitle) => {
            if (!activeEntry) return;
            const trimmed = nextTitle.trim();
            if (!trimmed) return;
            // Patch the form pointer + mirror the new title locally
            // so the viewer reflects it immediately. The
            // salon_documents row's displayName doesn't update from
            // here (no rename endpoint); the next markup save uploads
            // with this title and syncs both sides.
            onPointerUpdate?.(activeEntry, { filename: trimmed });
            setMarkupDisplayName(trimmed);
          }}
          // Pen icon is always shown so the action surface stays
          // consistent across entries. Click checks markupEligible:
          // legacy bare-string pointers or entries whose dual-write
          // hasn't run yet open a notification instead of the editor.
          onOpenMarkup={() => {
            if (!markupEligible) {
              notifyWarning(
                "This image is from before the form-Documents link was wired up, or the dual-write hasn't completed. Try re-uploading the image to enable markup.",
                { title: "Markup not available yet" },
              );
              return;
            }
            setMarkupOpen(true);
          }}
        />
      )}

      {/* Markup editor — separate fullscreen modal stacked over the
          carousel viewer. Mirrors the Documents tab + Medical Charts
          ChartEditor-in-Modal pattern exactly: bare ChartEditor, no
          header overlays, auto-close on Save (handleMarkupSave flips
          markupOpen=false). */}
      {markupOpen && markupEligible && activeEntry !== undefined && (
        <Modal
          opened
          onClose={() => setMarkupOpen(false)}
          fullScreen
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          padding={0}
          styles={{
            body: { height: "100%", display: "flex", flexDirection: "column" },
            content: { height: "100%" },
          }}
        >
          <ChartEditor
            key={activeDocKey ?? "form-markup"}
            baseImageUrl={viewerBlobUrl ?? activePreviewUrl ?? ""}
            diagramName={markupDisplayName ?? activeFilename ?? "Form image"}
            clientDisplayName={markupContext?.clientDisplayName}
            onBack={() => setMarkupOpen(false)}
            onSave={handleMarkupSave}
            onLoadVersions={handleLoadMarkupVersions}
            onLoadVersion={handleLoadMarkupVersion}
          />
        </Modal>
      )}
    </Stack>
  );
}
